'use client'

import type { ReactNode } from 'react'
import { ConsentDialog, ConsentManagerProvider } from '@c15t/nextjs'

/**
 * Coolmate design tokens for the c15t consent UI.
 *
 * Matches the existing cookie banner design:
 *   - White background card
 *   - Black "Accept All" primary button
 *   - Bordered "Reject All" secondary button
 *   - Gray-200 border, gray-700 muted text
 */
const coolmateTheme = {
  colors: {
    primary: '#000000',
    primaryHover: '#1a1a1a',
    surface: '#ffffff',
    surfaceHover: '#f9fafb',
    border: '#e5e7eb',
    borderHover: '#d1d5db',
    text: '#111827',
    textMuted: '#374151',
    textOnPrimary: '#ffffff',
    overlay: 'rgba(0, 0, 0, 0.4)',
    switchTrack: '#d1d5db',
    switchTrackActive: '#000000',
    switchThumb: '#ffffff',
  },
  radius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 2px hsla(0,0%,0%,0.05)',
    md: '0 4px 12px hsla(0,0%,0%,0.08)',
    lg: '0 -1px 0 0 #e5e7eb, 0 4px 16px hsla(0,0%,0%,0.08)',
  },
  typography: {
    // Use the site's primary body font across all dialog text
    fontFamily: 'var(--font-pangea), sans-serif',
  },
  slots: {
    // Ensure ConsentDialog floats above the cookie banner overlay (z-[9990]).
    // The Radix UI portal renders overlay + card as body-level siblings, so
    // each element needs its own z-index — the wrapper slot alone is not enough.
    consentDialog: {
      style: { zIndex: 10000 },
    },
    consentDialogOverlay: {
      style: { zIndex: 9995 },
    },
    consentDialogCard: {
      style: { zIndex: 10000 },
    },
    // Title larger than description
    consentDialogTitle: {
      style: { fontSize: '1.25rem', fontWeight: '700', lineHeight: '1.75rem' },
    },
    consentDialogDescription: {
      style: { fontSize: '0.875rem', lineHeight: '1.5rem' },
    },
    // Force active switch track to black (overrides c15t default blue)
    toggle: {
      style: { '--c15t-switch-track-active': '#000000' },
    },
    // Scope pill-radius to footer buttons only — leaves accordion category rows untouched
    consentDialogFooter: {
      style: { '--c15t-radius-md': '9999px' },
    },
    consentWidgetFooterSubGroup: {
      style: {
        '--c15t-radius-md': '9999px',
        display: 'flex',
        width: '100%',
        alignItems: 'center',
      },
    },
    // Move Save Settings to the far left
    buttonPrimary: {
      style: { order: -1, marginRight: 'auto' },
    },
  },
}

export function ConsentManagerClient({ children }: { children: ReactNode }) {
  const language = process.env.NEXT_PUBLIC_C15T_LANGUAGE ?? 'vi'
  const country = process.env.NEXT_PUBLIC_C15T_COUNTRY ?? 'VN'

  return (
    <ConsentManagerProvider
      options={{
        mode: 'hosted',
        backendURL: process.env.NEXT_PUBLIC_C15T_URL ?? 'http://localhost:3000',
        consentCategories: ['necessary', 'measurement', 'marketing', 'experience'],
        theme: coolmateTheme,
        overrides: { country, language },
      }}
    >
      <ConsentDialog hideBranding />
      {children}
    </ConsentManagerProvider>
  )
}
